import {createAsyncThunk} from '@reduxjs/toolkit';
import type {EmailOtpType} from '@supabase/supabase-js';
import {supabase, getAuthStore} from '../../lib/supabaseClient';
import {getJWTToken} from '../../lib/auth-token';
import {getWorkerApiUrl, apiFetch} from '../../lib/api';
import {debugLog, debugWarn} from '../../lib/debug';
import {ApiError, toApiError, toNetworkError} from '../../lib/apiError';
import {clearPendingSignup} from '../../lib/signupSession';
import {clearSessionScopedCaches} from '../../lib/sessionCleanup';
import type {SetPasswordRequest} from '../../backend-types/signup-api';
import type {VideoEditor} from '../../backend-types/user';
import type {AppUser} from './authSlice';

// Never let session restoration hang the app. On the desktop app getSession() could block
// indefinitely; on the web it reads localStorage, but it also performs the PKCE code exchange
// after an OAuth / email-link redirect, which is a network call.
const SESSION_RESTORE_TIMEOUT_MS = 3000;

export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async () => {
        // Resolve the stored session up front so `loading` stays true until we actually know
        // whether the visitor is signed in. Protected routes gate on this — without it, a
        // signed-in user who deep-links to an app URL is bounced to /login before the
        // onAuthStateChange listener has had a chance to fire.
    let session = null;
    try {
      const result = await Promise.race([
        supabase.auth.getSession(),
        new Promise<null>(resolve => setTimeout(() => resolve(null), SESSION_RESTORE_TIMEOUT_MS)),
      ]);
      session = result?.data?.session ?? null;
    } catch (error) {
      debugWarn('[initializeAuth] getSession failed:', error);
    }

    if (!session) {
      return {user: null, session: null, appUser: null};
    }

        // Restore the cached profile; onAuthStateChange refreshes it in the background.
    let appUser: AppUser | null = null;
    try {
      const store = await getAuthStore();
      appUser = (await store.get<AppUser>('app_user')) ?? null;
    } catch (error) {
      debugWarn('[initializeAuth] Could not read cached app_user:', error);
    }

    return {user: session.user, session, appUser};
  }
);

export const signIn = createAsyncThunk(
  'auth/signIn',
  async ({email, password}: { email: string; password: string }, {rejectWithValue}) => {
    try {
      const {data, error} = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

            // Fetch full user profile from the API
      let appUser: AppUser | null = null;
      try {
        const jwt = await getJWTToken();
        if (jwt) {
          const apiUrl = getWorkerApiUrl();
          const url = `${apiUrl}/rest/v1/user/profile`;

          const response = await apiFetch(url, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${jwt}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const profile = await response.json();
            appUser = profile.user || profile;
            const store = await getAuthStore();
            await store.set('app_user', appUser);
            await store.save();
          }
        }
      } catch (err) {
        debugWarn('[signIn] Could not fetch user profile:', err);
      }

      return {
        user: data.user,
        session: data.session,
        appUser,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || String(error));
    }
  }
);

/**
 * Absolute URL for an in-app path. Supabase needs a full URL for email redirects, and whatever we
 * send has to be in the project's redirect allowlist.
 */
function appUrl(path: string): string {
  const base = import.meta.env.VITE_APP_URL || window.location.origin;
  return `${base.replace(/\/$/, '')}${path}`;
}

/**
 * There is no `signUp` thunk, and there must not be one.
 *
 * Signup runs through Paddle: the card comes first and the subscription webhook is what creates the
 * account, so `supabase.auth.signUp()` is disabled at the auth layer and answers with a refusal. A
 * client-side call would be the one door around the checkout, which is exactly why it is shut. The
 * flow lives in `store/slices/signupThunks.ts` and `backend-types/signup-api.ts`.
 *
 * The 6-digit email-verification thunks went with it: nothing sends that email any more. A
 * provisioned account proves the address by clicking its one-time login link instead.
 */

/**
 * Sets the password for the account the current session belongs to, and clears the "no password yet"
 * flag on the profile row.
 *
 * This is the screen a freshly provisioned account lands on: it was created from a checkout, so it
 * has never had a password, and until it does the only way back in is another emailed link. It goes
 * through the worker rather than `supabase.auth.updateUser` because the flag lives on the CensorMe
 * user row, which only the worker writes.
 *
 * **Trust the response, not the JWT.** `censor_me_password_set` is stamped when the token is issued
 * and stays false until the session refreshes, so re-reading the claim here would say the write
 * never happened.
 */
export const setAccountPassword = createAsyncThunk<
  true,
  string,
  {rejectValue: ApiError}
>(
  'auth/setAccountPassword',
  async (password, {rejectWithValue}) => {
    try {
      const jwt = await getJWTToken();
      if (!jwt) {
        return rejectWithValue({status: 401, message: 'Your session has expired. Use your login link again.'});
      }

      const body: SetPasswordRequest = {password};
      const response = await apiFetch(`${getWorkerApiUrl()}/rest/v1/user/password`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        return rejectWithValue(toApiError(
          response.status,
          await response.text(),
          'Could not set your password'
        ));
      }

      debugLog('[setAccountPassword] Password set');
      // The signup is over — nothing should resume it after this.
      clearPendingSignup();
      return true;
    } catch (error) {
      return rejectWithValue(toNetworkError(error, 'Could not set your password'));
    }
  }
);

/**
 * Exchanges the `token_hash`/`type` pair from an emailed link for a session — the landing point for
 * both the signup login link (`type: 'magiclink'`) and the password-recovery link
 * (`type: 'recovery'`), at `/auth/confirm`.
 *
 * This is a **different mechanism** from the OAuth/PKCE `code` flow, which `detectSessionInUrl`
 * exchanges automatically before the app even finishes mounting. These links carry a token hash
 * instead, so nothing happens until this thunk calls `verifyOtp` explicitly — `detectSessionInUrl`
 * has nothing to act on for them.
 *
 * Sets `user`/`session` in the slice itself (see `authSlice.ts`) rather than waiting on the
 * `onAuthStateChange` listener in `App.tsx`: that listener also fires from this call and would
 * eventually write the same values, but only after the container has already tried to navigate,
 * which is exactly the race that would flash "Link expired" on `/reset-password` for a frame.
 */
export const confirmEmailLink = createAsyncThunk(
  'auth/confirmEmailLink',
  async ({tokenHash, type}: { tokenHash: string; type: EmailOtpType }, {rejectWithValue}) => {
    try {
      const {data, error} = await supabase.auth.verifyOtp({token_hash: tokenHash, type});

      if (error) {
        throw error;
      }

      debugLog('[confirmEmailLink] Verified', type, 'link');
      return {user: data.user, session: data.session, type};
    } catch (error: any) {
      return rejectWithValue(error?.message || 'This link is invalid or has expired.');
    }
  }
);

/**
 * Step 1 of a password reset: email a magic link. There is no code flow — the emailed link is the
 * only way through.
 *
 * **`redirectTo` stays pointed at `/reset-password`, and that is deliberate**, even though the link
 * is expected to land on `/auth/confirm`. Which one it actually hits is decided by the recovery email
 * template, not by this call:
 *
 *   - Template uses `{{ .SiteURL }}/auth/confirm?token_hash={{ .TokenHash }}&type=recovery` — the
 *     link goes straight to `/auth/confirm` and `redirectTo` is **ignored entirely**.
 *   - Template uses `{{ .ConfirmationURL }}` (the stock one) — the link goes to Supabase's verify
 *     endpoint, which redirects to `redirectTo` with tokens `detectSessionInUrl` picks up.
 *
 * So `/reset-password` is the value that is correct in the second case and harmless in the first.
 * Pointing it at `/auth/confirm` instead would break the stock template: that landing has no
 * `token_hash` to verify. `/auth/confirm` tolerates arriving with a session and no token anyway,
 * so neither template can strand a user.
 *
 * Whether the address actually has an account is deliberately never reported back — that answer is
 * an account-enumeration oracle, so the next screen says "if that address has an account". The one
 * failure worth surfacing is the rate limit, because hiding it just makes the user press the button
 * again and wonder why no email arrives.
 */
export const requestPasswordReset = createAsyncThunk(
  'auth/requestPasswordReset',
  async (email: string, {rejectWithValue}) => {
    const {error} = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: appUrl('/reset-password'),
    });

    if (error) {
      if (error.status === 429) {
        return rejectWithValue('Too many reset emails requested. Wait a few minutes and try again.');
      }
      debugWarn('[requestPasswordReset] Suppressed error:', error.message);
    }

    debugLog('[requestPasswordReset] Reset requested for', email);
    return email;
  }
);

/** GoTrue's password errors, said in a way a human can act on. */
function passwordErrorMessage(error: any): string {
  const message: string = error?.message || String(error);
  if (message.includes('weak_password') || message.includes('at least 6 characters')) {
    return 'Password must be at least 6 characters long.';
  }
  if (message.includes('should be different from the old password')) {
    return 'That is already your password. Pick a different one.';
  }
  if (message.includes('Auth session missing')) {
    return 'Your reset link has expired. Request a new one and try again.';
  }
  return message;
}

/**
 * Step 2 — sets the new password on the session the emailed link established (`detectSessionInUrl`
 * exchanges it on arrival, before this ever runs). The Supabase project has `secure_password_change`
 * off, so the old password is not required here.
 *
 * This is the *recovery* path, where the emailed link is the proof of identity. The account page
 * uses `changePassword` below, which asks for the current password instead.
 */
export const updatePassword = createAsyncThunk(
  'auth/updatePassword',
  async (password: string, {rejectWithValue}) => {
    try {
      const {data, error} = await supabase.auth.updateUser({password});

      if (error) {
        throw error;
      }

      debugLog('[updatePassword] Password changed');
      return {user: data.user};
    } catch (error: any) {
      return rejectWithValue(passwordErrorMessage(error));
    }
  }
);

/**
 * Password change from the account page, for a user who is already signed in and knows their
 * current password.
 *
 * **The re-authentication is ours, not GoTrue's.** The project has `secure_password_change = false`,
 * so `updateUser({password})` on a live session would succeed with no further proof — meaning anyone
 * who walks up to an unlocked browser could change the password and lock the owner out permanently.
 * Checking the current password with `signInWithPassword` first closes that. It is a client-side
 * check and an attacker calling GoTrue directly still bypasses it; the real fix is
 * `secure_password_change = true` on the project (docs/BACKEND_REQUESTS.md §16).
 */
export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (
    {email, currentPassword, newPassword}: { email: string; currentPassword: string; newPassword: string },
    {rejectWithValue}
  ) => {
    try {
      const {error: reauthError} = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });

      if (reauthError) {
        return rejectWithValue('That is not your current password.');
      }

      const {data, error} = await supabase.auth.updateUser({password: newPassword});

      if (error) {
        throw error;
      }

      debugLog('[changePassword] Password changed from the account page');
      return {user: data.user};
    } catch (error: any) {
      return rejectWithValue(passwordErrorMessage(error));
    }
  }
);

/**
 * Email change, done against **Supabase auth** — which is the part that was missing.
 *
 * `updateUserProfile` writes `users.email` in the CensorMe database and nothing else, so before
 * this existed the account page moved the displayed address while the sign-in identity stayed on
 * the old one: the user would be locked out of the address they thought they had, and password
 * resets would keep going to the old inbox. Auth is the source of truth; the database row follows
 * it (see `syncProfileEmail`).
 *
 * Supabase emails a confirmation to the **new** address and the change only lands once it is
 * clicked — `double_confirm_changes` is off, so the old address is not asked. There is no code
 * flow here: the email-change template has no `{{ .Token }}` in it (docs/BACKEND_REQUESTS.md §16),
 * so the link is the only way through, and `/account` has to be in the redirect allowlist.
 */
export const changeEmail = createAsyncThunk(
  'auth/changeEmail',
  async (email: string, {rejectWithValue}) => {
    try {
      const {error} = await supabase.auth.updateUser(
        {email},
        {emailRedirectTo: appUrl('/account')}
      );

      if (error) {
        throw error;
      }

      debugLog('[changeEmail] Confirmation sent to', email);
      return email;
    } catch (error: any) {
      const message: string = error?.message || String(error);
      if (error?.status === 429) {
        return rejectWithValue('Too many emails requested. Wait a few minutes and try again.');
      }
      if (message.includes('already been registered') || message.includes('already exists')) {
        return rejectWithValue('That email address is already in use.');
      }
      return rejectWithValue(message);
    }
  }
);

/**
 * Profile-row edits. **Only `name` should ever come from a form here.**
 *
 * The endpoint will happily write `users.email`, but doing that from a form is what desynchronised
 * the account page from Supabase auth in the first place — the row said one address while sign-in
 * and password reset still used another. `email` stays supported for exactly one caller,
 * `syncProfileEmail`, which copies the address *down* from auth after a confirmed change.
 *
 * Note the method: the worker exposes this as PATCH (see backend references), while the web app's
 * CLAUDE.md documents PUT for the same path. PATCH is what actually answers today —
 * see docs/BACKEND_REQUESTS.md for the reconciliation request.
 */
export const updateUserProfile = createAsyncThunk(
  'auth/updateUserProfile',
  async ({name, email}: { name?: string; email?: string }, {rejectWithValue}) => {
    try {
      const jwt = await getJWTToken();
      if (!jwt) {
        throw new Error('No authentication token available');
      }

      // The worker treats any key it receives as an edit, so only send what was actually given.
      const body: Record<string, string> = {};
      if (name !== undefined) {
        body.name = name;
      }
      if (email !== undefined) {
        body.email = email;
      }

      const response = await apiFetch(`${getWorkerApiUrl()}/rest/v1/user/profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to update profile (${response.status})`);
      }

      const data = await response.json();
      const updated = (data.user || data) as AppUser;

      try {
        const store = await getAuthStore();
        await store.set('app_user', updated);
        await store.save();
      } catch (error) {
        debugWarn('[updateUserProfile] Could not cache updated profile:', error);
      }

      return updated;
    } catch (error: any) {
      return rejectWithValue(error?.message || String(error));
    }
  }
);

/**
 * Video editor preference — one PATCH `/rest/v1/user/profile`.
 *
 * `video_editor` accepts `null` to clear the preference back to unknown — not a fourth editor, see
 * `backend-types/user.ts`. The response is what's trusted, never a value computed here.
 *
 * The profile also carries `resolve_warning_acknowledged`, which this deliberately never sends: the
 * DaVinci Resolve warning dialog it recorded is gone, because Resolve does not hang on a media
 * reference it cannot find the way that dialog claimed.
 */
export const updateVideoEditorPreference = createAsyncThunk<
  AppUser,
  {videoEditor?: VideoEditor | null},
  {rejectValue: ApiError}
>(
  'auth/updateVideoEditorPreference',
  async ({videoEditor}, {rejectWithValue}) => {
    try {
      const jwt = await getJWTToken();
      if (!jwt) {
        return rejectWithValue({status: 401, message: 'Your session has expired. Sign in again.'});
      }

      const body: Record<string, unknown> = {};
      if (videoEditor !== undefined) {
        body.video_editor = videoEditor;
      }

      const response = await apiFetch(`${getWorkerApiUrl()}/rest/v1/user/profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        return rejectWithValue(toApiError(
          response.status,
          await response.text(),
          'Could not save your video editor preference'
        ));
      }

      const data = await response.json();
      const updated = (data.user || data) as AppUser;

      try {
        const store = await getAuthStore();
        await store.set('app_user', updated);
        await store.save();
      } catch (error) {
        debugWarn('[updateVideoEditorPreference] Could not cache updated profile:', error);
      }

      return updated;
    } catch (error) {
      return rejectWithValue(toNetworkError(error, 'Could not save your video editor preference'));
    }
  }
);

/**
 * Copies the address *down* from Supabase auth onto the CensorMe profile row when the two disagree.
 *
 * Runs on the account page. It closes the loop after a confirmed email change — the confirmation
 * link updates auth and nothing else — and it also self-heals accounts that were desynchronised by
 * the old account form, which wrote the row without ever telling auth. Auth always wins: it is what
 * sign-in and password reset actually use.
 */
export const syncProfileEmail = createAsyncThunk(
  'auth/syncProfileEmail',
  async (_, {getState, dispatch}) => {
    const state: any = getState();
    const authEmail: string | undefined = state.auth.user?.email?.toLowerCase();
    const rowEmail: string | undefined = state.auth.appUser?.email?.toLowerCase();

    if (!authEmail || !rowEmail || authEmail === rowEmail) {
      return null;
    }

    debugLog('[syncProfileEmail] Profile row has', rowEmail, 'but auth has', authEmail, '— healing');
    return await dispatch(updateUserProfile({email: authEmail})).unwrap();
  }
);

export const updateSession = createAsyncThunk(
  'auth/updateSession',
  async (session: any) => {
    if (!session) {
            // No session, clear everything
      const store = await getAuthStore();
      await store.delete('app_user');
      await store.save();
      return {
        user: null,
        session: null,
        appUser: null,
      };
    }

        // Try to restore appUser from Tauri Store first (fast)
    let appUser: AppUser | null = null;
    try {
      const store = await getAuthStore();
      const storedAppUser = await store.get<AppUser>('app_user');
      if (storedAppUser) {
        appUser = storedAppUser;
      }
    } catch (err) {
      debugWarn('[updateSession] Could not get app_user from store:', err);
    }

        // Fetch fresh user data from database in the background (don't wait for it)
        // This updates the cached data but doesn't block the login
    (async () => {
      try {
        const {data: userData, error: userError} = await supabase
          .from('users')
          .select('*')
          .eq('email', session.user.email?.toLowerCase())
          .single();

        if (!userError && userData) {
          const store = await getAuthStore();
          await store.set('app_user', userData);
          await store.save();
          debugLog('[updateSession] Updated app_user in background');
        }
      } catch (err) {
                // Silently ignore errors - this is best-effort background update
        debugLog('[updateSession] Background user fetch failed:', err);
      }
    })();

    return {
      user: session.user,
      session: session,
      appUser,
    };
  }
);

/**
 * Ends the session and takes the whole of it with it.
 *
 * Signing out on the web does not reload the page, so anything not explicitly dropped here survives
 * into the next account signed in on this browser. That is not theoretical: the previous account's
 * project id lived on in `censorme.currentProjectId` and in `projects.currentProject`, so every
 * project-scoped fetch for the new account came back `403 You do not own this project`.
 *
 * Three things have to go, in three different places:
 *   - auth tokens and the cached profile — here;
 *   - client-side preferences and module-level HTTP caches — `clearSessionScopedCaches()`;
 *   - the Redux store — the root reducer in `store.ts`, on `auth/signOut/fulfilled`.
 */
export const signOut = createAsyncThunk(
  'auth/signOut',
  async () => {
    debugLog('[signOut] Signing out...');

    // Project preferences and ETag caches. Not user-scoped, so they taint the next session.
    clearSessionScopedCaches();

    // A signup in flight belongs to whoever was at the keyboard, not to the next visitor.
    clearPendingSignup();

        // Clear the Tauri Store first
    const store = await getAuthStore();
    await store.delete('app_user');
    await store.delete('supabase.auth.token');
    await store.delete('supabase.auth.token-code-verifier');
    await store.save();
    debugLog('[signOut] Tauri Store cleared');

        // Clear localStorage (for any legacy data or fallback storage)
    try {
      localStorage.removeItem('app_user');
            // Clear any Supabase-related localStorage keys
      Object.keys(localStorage).forEach((key) => {
        if (key.includes('supabase') || key.startsWith('sb-')) {
          localStorage.removeItem(key);
        }
      });
      debugLog('[signOut] localStorage cleared');
    } catch (err) {
      debugWarn('[signOut] localStorage cleanup failed:', err);
    }

        // Call Supabase signOut LAST (don't await it - just fire and forget)
        // This clears in-memory state but might hang, so we don't wait for it
    supabase.auth.signOut({scope: 'local'}).catch((err) => {
      debugWarn('[signOut] Supabase signOut error (non-blocking):', err);
    });
    debugLog('[signOut] Supabase signOut called (non-blocking)');

    debugLog('[signOut] Done - all auth data cleared');
    return null;
  }
);

export const fetchUserProfile = createAsyncThunk(
  'auth/fetchUserProfile',
  async (_, {rejectWithValue}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWorkerApiUrl();
      const url = `${apiUrl}/rest/v1/user/profile`;

      debugLog('[Auth] Fetching user profile');
      const response = await apiFetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
      });

      debugLog('[Auth] Profile fetch response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`Failed to fetch user profile: ${errorText}`);
      }

      const data = await response.json();
      debugLog('[Auth] User profile fetched:', data);

      return (data.user || data) as AppUser;
    } catch (error) {
      const errorStr = String(error);
      console.error('[Auth] Error fetching user profile:', errorStr);
      return rejectWithValue(errorStr);
    }
  }
);

export const setWelcomePagePreference = createAsyncThunk(
  'auth/setWelcomePagePreference',
  async (showWelcomePage: boolean, {rejectWithValue, getState}) => {
    try {
      const jwt = await getJWTToken();

      if (!jwt) {
        throw new Error('No authentication token available');
      }

      const apiUrl = getWorkerApiUrl();
      const url = `${apiUrl}/rest/v1/user/profile`;

      debugLog('[Auth] Setting welcome page preference:', showWelcomePage);
      const response = await apiFetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          welcome_page: showWelcomePage,
        }),
      });

      debugLog('[Auth] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        throw new Error(`Failed to set welcome page preference: ${errorText}`);
      }

      const data = await response.json();
      debugLog('[Auth] Welcome page preference set:', data);

            // Return the updated user data from response or from state
      const userData = data.user || data;
      if (userData) {
        return userData as AppUser;
      }

            // Fallback to updating state locally
      const state: any = getState();
      if (state.auth.appUser) {
        return {
          ...state.auth.appUser,
          welcome_page: showWelcomePage,
        };
      }

      return null;
    } catch (error) {
      const errorStr = String(error);
      console.error('[Auth] Error setting welcome page preference:', errorStr);
      return rejectWithValue(errorStr);
    }
  }
);
